/**
 * Bandeja de conflictos (solo Administración).
 *
 * Muestra la comparación entre lo capturado en el dispositivo y el estado del
 * servidor, la causa, el impacto monetario y únicamente las acciones seguras para
 * esa causa. Requiere conexión: resolver un conflicto es una decisión del servidor.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ConflictDto } from '@senm/contracts';
import {
  formatInstant,
  formatMoney,
  fromRow,
  RESOLUTION_ACTION_LABELS,
  type ResolutionAction,
} from '@senm/domain';
import { ApiRequestError, apiRequest } from '../api/client.js';
import type { StoredSession } from '../local/session.js';

export function ConflictsPage(props: { session: StoredSession; conexion: string }): JSX.Element {
  const [conflictos, setConflictos] = useState<ConflictDto[]>([]);
  const [estado, setEstado] = useState<'ABIERTO' | 'RESUELTO'>('ABIERTO');
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [expandido, setExpandido] = useState<string | null>(null);
  const [nota, setNota] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const pagina = await apiRequest<{ items: ConflictDto[] }>(`/v1/conflicts?status=${estado}`);
      setConflictos(pagina.items);
    } catch (problema) {
      setError(
        problema instanceof ApiRequestError && problema.isNetworkFailure
          ? 'La bandeja de conflictos necesita conexión con el servidor.'
          : 'No fue posible cargar los conflictos.',
      );
    } finally {
      setCargando(false);
    }
  }, [estado]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const resolver = async (conflicto: ConflictDto, accion: ResolutionAction): Promise<void> => {
    const confirmar = window.confirm(
      `¿Confirma la acción "${RESOLUTION_ACTION_LABELS[accion]}" para este caso?\n\n` +
        'La evidencia original se conserva; la resolución se registra como una acción nueva.',
    );
    if (!confirmar) return;

    try {
      await apiRequest(`/v1/conflicts/${conflicto.id}/resolve`, {
        method: 'POST',
        body: { action: accion, note: nota.trim() === '' ? null : nota.trim(), correctedPaymentId: null },
      });
      setNota('');
      setExpandido(null);
      await cargar();
    } catch (problema) {
      setError(problema instanceof ApiRequestError ? problema.message : 'No fue posible resolver el caso.');
    }
  };

  return (
    <>
      <h1>Conflictos</h1>
      <p className="ayuda">
        Casos que no pudieron aplicarse automáticamente. Ningún pago se pierde: aquí se decide qué
        hacer con cada uno.
      </p>

      <div className="pila">
        <button
          type="button"
          className="boton"
          aria-pressed={estado === 'ABIERTO'}
          onClick={() => setEstado('ABIERTO')}
        >
          Abiertos
        </button>
        <button
          type="button"
          className="boton"
          aria-pressed={estado === 'RESUELTO'}
          onClick={() => setEstado('RESUELTO')}
        >
          Resueltos
        </button>
      </div>

      {props.conexion === 'SIN_CONEXION' && (
        <div className="aviso-caja" role="status">
          Sin conexión: esta bandeja no puede consultarse ni resolverse ahora.
        </div>
      )}
      {error !== null && (
        <div className="aviso-caja error" role="alert">
          {error}
        </div>
      )}

      {cargando ? (
        <p className="vacio">Cargando…</p>
      ) : conflictos.length === 0 ? (
        <p className="vacio">No hay casos {estado === 'ABIERTO' ? 'abiertos' : 'resueltos'}.</p>
      ) : (
        conflictos.map((conflicto) => (
          <section className="tarjeta" key={conflicto.id} aria-label={`Conflicto ${conflicto.id}`}>
            <div className="pila">
              <span className={`marca-estado ${conflicto.requiresReview ? 'error' : 'aviso'}`}>
                {conflicto.requiresReview ? '! Requiere revisión' : '⚠ Divergencia'}
              </span>
              <span className="marca-estado">{conflicto.causeLabel}</span>
              {conflicto.monetaryImpact !== null && (
                <span className="marca-estado">
                  Impacto: {formatMoney(fromRow(conflicto.monetaryImpact.minor, conflicto.monetaryImpact.currency))}
                </span>
              )}
            </div>

            <p>
              <strong>{conflicto.studentName ?? 'Estudiante desconocido'}</strong> · capturado por{' '}
              {conflicto.capturedByUserName ?? 'usuario desconocido'} ·{' '}
              {formatInstant(conflicto.detectedAt, props.session.user.timeZone)}
            </p>

            <button
              type="button"
              className="boton"
              aria-expanded={expandido === conflicto.id}
              onClick={() => setExpandido(expandido === conflicto.id ? null : conflicto.id)}
            >
              {expandido === conflicto.id ? 'Ocultar comparación' : 'Ver comparación'}
            </button>

            {expandido === conflicto.id && (
              <div className="rejilla" style={{ marginTop: 12 }}>
                <div>
                  <h2>Lo que se capturó en el dispositivo</h2>
                  <pre className="tarjeta" style={{ overflowX: 'auto', fontSize: '0.8rem' }}>
                    {JSON.stringify(conflicto.localSnapshot, null, 2)}
                  </pre>
                </div>
                <div>
                  <h2>Estado del servidor al recibirlo</h2>
                  <pre className="tarjeta" style={{ overflowX: 'auto', fontSize: '0.8rem' }}>
                    {JSON.stringify(conflicto.serverSnapshot, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {conflicto.status === 'ABIERTO' && (
              <>
                <label>
                  <span>Nota de resolución (queda en la auditoría)</span>
                  <input value={nota} onChange={(evento) => setNota(evento.target.value)} maxLength={500} />
                </label>
                <div className="pila">
                  {/* Solo las acciones seguras para esta causa: el servidor rechaza el resto. */}
                  {conflicto.availableActions.map((accion) => (
                    <button
                      key={accion}
                      type="button"
                      className={accion === 'RECHAZAR_LOCAL' ? 'boton' : 'boton primario'}
                      onClick={() => void resolver(conflicto, accion)}
                    >
                      {RESOLUTION_ACTION_LABELS[accion]}
                    </button>
                  ))}
                </div>
              </>
            )}

            {conflicto.status === 'RESUELTO' && (
              <p className="ayuda">
                Resuelto por {conflicto.resolvedByUserName ?? 'Administración'} —{' '}
                {conflicto.resolutionAction !== null ? RESOLUTION_ACTION_LABELS[conflicto.resolutionAction] : ''}.{' '}
                {conflicto.resolutionNote}
              </p>
            )}
          </section>
        ))
      )}
    </>
  );
}
